import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { User } from 'app/auth/models';
import { RouterService } from 'app/global/router.service';
import { LocalStorageKeys, StorageService } from 'app/global/storage.service';
import { UserSettings } from 'app/global/user-settings.dto';
import { EditorEmitterService } from 'app/main/pages/events/editor-emitter.service';
import { AuthFacade } from 'app/store/auth.facade';
import { BehaviorSubject, firstValueFrom, Observable, of, skipUntil, switchMap, take, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ILoginForm } from '../../main/pages/authentication/auth-login/auth-login.component';
import { HttpActionState } from '../../main/pages/users/userslisting/store/users-listing.reducers';
import * as AuthSelectors from '../../store/selectors/auth.selectors';
import { AuthHelperService } from '../helpers/auth-helper.service';
import { IOtpForm } from 'app/main/pages/authentication/verify-otp/verify-otp.component';
import { IframeAbleService } from '../helpers/iframeable.service';
import * as AuthActions from "../../store/actions/auth.action";
import * as UserSettingsActions from "../../store/actions/user.actions";

export interface IRegisterForm {
  name: string;
  email: string;
  terms: boolean;
}

//for catch:
@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  //public
  public currentUser: Observable<User>;

  //private
  private currentUserSubject: BehaviorSubject<User>;
  private bypassHttpInterceptor: HttpClient;
  /**
   * @param http
   * @param _authFacade
   * @param editorEmitter
   * @param _storageService
   * @param _modalService
   * @param _routerService
   * @param _store
   * @param _httpBackend
   * @param authHelperService
   * @param iframeAbleService
   */
  constructor(
    private http: HttpClient,
    private _authFacade: AuthFacade,
    private editorEmitter: EditorEmitterService,
    private _storageService: StorageService,
    private _modalService: NgbModal,
    private _routerService: RouterService,
    private _store: Store,
    private _httpBackend: HttpBackend,
    private authHelperService: AuthHelperService,
    private iframeAbleService: IframeAbleService
  ) {
    this.bypassHttpInterceptor = new HttpClient(this._httpBackend);
  }

  /**
   * 
   * @param key 
   * @param value 
   */
  public saveData(key: string, value: string) {
    localStorage.setItem(key, value);
  }

  /**
   * 
   * @param key 
   */
  getData(key: string) {
    return localStorage.getItem(key)
  }

  /**
   * 
   * @param key 
   */
  public removeData(key: string) {
    localStorage.removeItem(key);
  }

  /**
   * 
   */
  async isLoggedIn() {
    //TODO: temporary fix while BE will allow to connect from localhost
    const token = this.authHelperService.getLocalDevToken();
    if (token) {
      return new Promise<boolean>(resolve => resolve(true))
    }

    return firstValueFrom(this._store.select(AuthSelectors.isLoggedIn)
      .pipe(
        skipUntil(this._store.select(AuthSelectors.loadingUserDataStatus)
          .pipe(map(x => x === HttpActionState.Init))
        )
      ));
  }


  /**
   *  login the external user | AMIND | SOCIAL LOGIN
   */
  loginMe(): Observable<UserSettings> {
    return this.http.get<UserSettings>('me')
  }

  finalizeSocialLogin(): Observable<UserSettings> {
    return this.http.get<UserSettings>('finalize-social-login')
  }

  /*
  *  Set up csrf token endpoint
  * */

  getCSRFCookie() {
    const apiUrl = environment.apiUrl;
    //TODO: temporary fix while BE will allow to connect from localhost
    const token = this.authHelperService.getLocalDevToken();
    if (token) {
      return of();
    }


    return this.bypassHttpInterceptor.get(`${apiUrl}csrf-cookie`, {
      responseType: 'text',
      withCredentials: true,
    });
  }


  getSessionFromToken(token: string) {
    return this.getCSRFCookie().pipe(
      switchMap(() => this.http.post<UserSettings>(`exchange-token-for-session`,
        null,
        {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        })

      )
    );
  }

  getSessionFromTokenGeneral(token: string) {
    return this.getCSRFCookie().pipe(
      switchMap(() => this.http.post<UserSettings>(`login-via-token`,
        { token_identifier: 'exchange-token' },
        {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        })

      )
    );
  }


  /**
   *  do user login
   * @param credential email and password
   */
  doLogin(credential: ILoginForm) {
    return this.getCSRFCookie().pipe(
      switchMap(() => this.http.post<UserSettings>('login', credential))
    );
  }


  /**
   * Verifies the provided OTP (One-Time Password) by sending it to the server.
   * 
   * @param otp - The OTP form data to be verified.
   * @returns An Observable that emits the user settings upon successful verification.
   */
  verifyOtp(otp: IOtpForm): Observable<UserSettings> {
    return this.getCSRFCookie().pipe(
      switchMap(() => this.http.post<UserSettings>('auth/verify-otp', otp))
    );
  }



  /**
   * Resends the OTP (One-Time Password) to the user.
   * 
   * This method first retrieves the CSRF cookie by calling `getCSRFCookie()`.
   * Once the CSRF cookie is obtained, it sends a POST request to the 'auth/verify-otp' endpoint
   * to resend the OTP.
   * 
   * @returns An Observable that emits the response of the POST request to 'auth/verify-otp'.
   */
  resendOtp() {
    return this.getCSRFCookie().pipe(
      switchMap(() => this.http.post('auth/resend-otp', {}))
    );
  }


  /**
   * do register
   * @param registerDetails 
   */
  doRegister(registerDetails: IRegisterForm) {
    return this.getCSRFCookie()
      .pipe(
        switchMap(() => this.http.post<UserSettings>('register', registerDetails))
      )
  }

  /**
   * logout the user
   */
  logout() {
    return this.http.post('logout', {});
  }

  logMeOut() {
    this._store.dispatch(AuthActions.logout());
  }

  /**
   * Fetches the token for Hubspot chat.
   */
  hubspotChat() {
    return this.http.get('hubspots/chat', {});
  }

  logoutActions() {
    this.editorEmitter.setUnsavedChangesFlag(false);
    this._modalService.dismissAll(); // If there has active modals then i closed it on logout
    this._storageService.removeItems([LocalStorageKeys.USER, LocalStorageKeys.STAY_LOGGED_IN]);
    this._authFacade.isThirdPartyLogin$.pipe(take(1)).subscribe(async (isThirdPartyLogin) => {
      const isIframeableLogin = await this.iframeAbleService.isIframeAbleLoggedIn();
      const url = isThirdPartyLogin ? '/crm-login' : isIframeableLogin ? '/iframeable/login' : '/login';
      this._routerService.redirectCrmUserToRoute(url);
    });
    this._authFacade.clearState();
    this._store.dispatch(new UserSettingsActions.STOP_USER_INACTIVITY_DETECTION());
    this._store.dispatch(new UserSettingsActions.CLEAR_STATE());
  }

  erroHandler(error: HttpErrorResponse): Observable<never> {
    console.log(error, 'error');
    return throwError(error);
  }

  forgotPassword(email: string) {
    return this.http.post('forgotpassword', {
      email: email
    })
  }

  resetPassword(password: string, confirmPassword: string, token: string) {
    return this.http.post('reset-password', {
      password: password,
      password_confirmation: confirmPassword,
      token: token
    });
  }

  emailVerfiy(token: string) {
    return this.getCSRFCookie().pipe(
      switchMap(() =>
        this.http.post(
          'verify-email',
          { token }
        )
      )
    );
  }

  refreshSession() {
    return this.http.post<void>('keep-authenticated', {});
  }

  initiateSsoLogin(email: string) {
    return this.getCSRFCookie().pipe(
      switchMap(() =>
        this.http.post(
          'auth/entra-id/redirect',
          { email }
        )
      )
    );
  }
}
